import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * GOAT: Bundle module (sections/bundle-module.liquid + snippets/bundle-module-drawer.liquid +
 * snippets/bundle-module-item-picker.liquid).
 *
 * A row of merchant-configured bundle cards. Each card's CTA opens the section's
 * `<theme-drawer always-modal no-persist>` on that bundle's panel, where every bundle item has
 * its own colour + size radios. The drawer CTA adds every item as its OWN cart line in one
 * `/cart/add.js` batch (mirroring product-form.js's batch path), so line-level discounts and
 * promo codes apply normally — there is no bundle discount.
 *
 * Analytics hooks — each is dispatched as a CustomEvent on `document` and mirrored into
 * window.dataLayer when a dataLayer array is present. No tracking code lives here. Every payload
 * carries { bundleId, bundleName, sectionId }; prices are integer cents of `currency`.
 *
 *   goat:bundle:card-view          {}                                               — card ≥50% visible (once, not in the theme editor)
 *   goat:bundle:cta-click          {}                                               — card CTA clicked
 *   goat:bundle:drawer-open        {}                                               — drawer opened
 *   goat:bundle:variant-select     { productId, variantId, option, value, available }
 *   goat:bundle:add-to-cart        { currency, total, items: [{ productId, variantId, quantity, price }] } — after the add succeeds
 *   goat:bundle:add-to-cart-error  { message }                                      — the add failed
 *   goat:bundle:cart-lines         { currency, lines: [{ key, productId, variantId, quantity, price }] }   — the bundle's cart lines after an add
 *
 * Every bundle line carries hidden `_bundle_module_id` / `_bundle_module_name` properties (underscore
 * keys are skipped by snippets/cart-products.liquid) so bundle lines stay identifiable in the cart
 * and on the order. Not `_bundle_id`, so a cart that groups discounted bundles never claims them.
 *
 * Note: no `on:*` attributes and no ref attributes beyond theme-drawer's own `panel` — all hooks are
 * `data-*` attributes wired with addEventListener.
 */

/**
 * @typedef {object} BundleVariant
 * @property {number} id
 * @property {string} title
 * @property {string[]} options
 * @property {boolean} available
 * @property {number} price
 * @property {number} compare_at_price
 * @property {string | null} image
 */

/**
 * @typedef {object} ItemState
 * @property {BundleVariant[]} variants
 * @property {number[]} positions - Rendered option positions (1-based), ascending.
 * @property {BundleVariant | null} variant - The currently resolved variant.
 */

class BundleModuleComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {WeakMap<HTMLElement, ItemState>} */
  #items = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });

    for (const item of this.querySelectorAll('[data-bundle-item]')) {
      this.#initItem(/** @type {HTMLElement} */ (item));
    }
    for (const panel of this.querySelectorAll('[data-bundle-panel]')) {
      this.#updatePanel(/** @type {HTMLElement} */ (panel));
    }

    this.#observeCards();

    const track = this.#track;
    if (track) {
      track.addEventListener('scroll', this.#updateArrows, { passive: true, signal });
      this.#resizeObserver = new ResizeObserver(this.#updateArrows);
      this.#resizeObserver.observe(track);
      this.#updateArrows();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#observer?.disconnect();
    this.#observer = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /** @returns {HTMLElement | null} */
  get #track() {
    return this.querySelector('[data-bundle-track]');
  }

  #updateArrows = () => {
    const track = this.#track;
    if (!track) return;

    const scrollLeft = Math.round(track.scrollLeft);
    const maxScroll = track.scrollWidth - track.clientWidth;
    this.classList.toggle('bundle-module--has-overflow', maxScroll > 1);

    const prev = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-bundle-prev]'));
    const next = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-bundle-next]'));
    if (prev) prev.disabled = scrollLeft <= 1;
    if (next) next.disabled = scrollLeft >= maxScroll - 1;
  };

  /** @param {number} direction - 1 for next, -1 for previous. */
  #scrollTrack(direction) {
    const track = this.#track;
    const card = track?.querySelector('[data-bundle-id]');
    if (!track || !card) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  /** @returns {(HTMLElement & { open?: () => void, close?: () => Promise<void>, isOpen?: boolean }) | null} */
  get #drawer() {
    return this.querySelector('theme-drawer.bundle-module-drawer');
  }

  get #sectionId() {
    return this.dataset.sectionId || '';
  }

  /**
   * GOAT: dispatches a named analytics hook on document.
   * @param {string} name
   * @param {object} detail
   */
  #emit(name, detail) {
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
    if (!Array.isArray(window.dataLayer)) return;
    // GTM merges arrays into its data model, so clear them or a smaller bundle inherits stale entries.
    const arrays = Object.keys(detail).filter((key) => Array.isArray(detail[key]));
    if (arrays.length) window.dataLayer.push(Object.fromEntries(arrays.map((key) => [key, null])));
    window.dataLayer.push({ event: name, ...detail });
  }

  get #currency() {
    return this.dataset.currency || 'USD';
  }

  /**
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    return formatMoney(cents, format, this.#currency);
  }

  /**
   * @param {string} bundleId
   * @returns {string}
   */
  #bundleName(bundleId) {
    const panel = this.querySelector(`[data-bundle-panel="${CSS.escape(bundleId)}"]`);
    return /** @type {HTMLElement | null} */ (panel)?.dataset.bundleName || '';
  }

  #observeCards() {
    const cards = this.querySelectorAll('[data-bundle-id]');
    if (!cards.length || !('IntersectionObserver' in window) || window.Shopify?.designMode) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const card = /** @type {HTMLElement} */ (entry.target);
          this.#observer?.unobserve(card);
          this.#emit('goat:bundle:card-view', {
            bundleId: card.dataset.bundleId,
            bundleName: card.dataset.bundleName,
            sectionId: this.#sectionId,
          });
        }
      },
      { threshold: 0.5 }
    );

    for (const card of cards) this.#observer.observe(card);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    if (!target) return;

    const arrow = target.closest('[data-bundle-prev], [data-bundle-next]');
    if (arrow) {
      this.#scrollTrack(arrow.hasAttribute('data-bundle-next') ? 1 : -1);
      return;
    }

    const openButton = /** @type {HTMLElement | null} */ (target.closest('[data-bundle-open]'));
    if (openButton) {
      event.preventDefault();
      this.#openBundle(openButton.dataset.bundleOpen || '');
      return;
    }

    if (target.closest('[data-bundle-close]')) {
      event.preventDefault();
      this.#drawer?.close?.();
      return;
    }

    const atcButton = /** @type {HTMLButtonElement | null} */ (target.closest('[data-bundle-atc]'));
    if (atcButton) {
      event.preventDefault();
      this.#addBundleToCart(atcButton);
    }
  };

  /** @param {string} bundleId */
  async #openBundle(bundleId) {
    if (!bundleId) return;
    const bundleName = this.#bundleName(bundleId);
    const detail = { bundleId, bundleName, sectionId: this.#sectionId };

    this.#emit('goat:bundle:cta-click', detail);

    for (const panel of this.querySelectorAll('[data-bundle-panel]')) {
      const el = /** @type {HTMLElement} */ (panel);
      el.hidden = el.dataset.bundlePanel !== bundleId;
      const error = el.querySelector('[data-bundle-error]');
      if (error instanceof HTMLElement) error.hidden = true;
      if (!el.hidden) this.#resyncPanel(el);
    }

    await customElements.whenDefined('theme-drawer');
    const drawer = this.#drawer;
    if (!drawer?.open) return;

    drawer.open();
    this.#emit('goat:bundle:drawer-open', detail);
  }

  /**
   * Back without bfcache can restore radios after #initItem seeded state, so re-read them.
   * @param {HTMLElement} panel
   */
  #resyncPanel(panel) {
    for (const item of panel.querySelectorAll('[data-bundle-item]')) {
      const el = /** @type {HTMLElement} */ (item);
      const state = this.#items.get(el);
      if (!state) continue;
      const variant = this.#resolveVariant(state, this.#selectedOptions(el));
      if (variant) {
        state.variant = variant;
      } else if (state.variant) {
        this.#checkVariant(el, state, state.variant);
      }
      this.#syncItem(el);
    }
    this.#updatePanel(panel);
  }

  /** @param {HTMLElement} item */
  #initItem(item) {
    const json = item.querySelector('script[data-bundle-variants]');
    /** @type {BundleVariant[]} */
    let variants = [];
    try {
      variants = JSON.parse(json?.textContent || '[]');
    } catch (error) {
      console.warn('[bundle-module] Could not parse bundle variants', error);
    }

    const positions = [
      ...new Set(
        [...item.querySelectorAll('input[data-bundle-option]')].map((input) =>
          Number(/** @type {HTMLInputElement} */ (input).dataset.bundleOption)
        )
      ),
    ].sort((a, b) => a - b);

    const currentId = Number(item.dataset.variantId);
    const variant = variants.find((v) => v.id === currentId) || variants[0] || null;

    this.#items.set(item, { variants, positions, variant });
    this.#syncItem(item);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('input[data-bundle-option]')) return;

    const item = /** @type {HTMLElement | null} */ (input.closest('[data-bundle-item]'));
    if (!item) return;
    const state = this.#items.get(item);
    if (!state) return;

    let variant = this.#resolveVariant(state, this.#selectedOptions(item));
    if (!variant) {
      // No variant has the new value with the other selections: snap them to one that does.
      variant = this.#resolveVariant(state, new Map([[Number(input.dataset.bundleOption), input.value]]));
      if (variant) this.#checkVariant(item, state, variant);
    }
    state.variant = variant;
    this.#syncItem(item);

    const panel = /** @type {HTMLElement | null} */ (item.closest('[data-bundle-panel]'));
    if (panel) this.#updatePanel(panel);

    this.#emit('goat:bundle:variant-select', {
      bundleId: panel?.dataset.bundlePanel || '',
      bundleName: panel?.dataset.bundleName || '',
      sectionId: this.#sectionId,
      productId: Number(item.dataset.productId),
      variantId: state.variant?.id ?? null,
      option: input.closest('fieldset')?.querySelector('.bundle-module-drawer__option-label')?.textContent?.replace(':', '').trim() || '',
      value: input.value,
      available: Boolean(state.variant?.available),
    });
  };

  /**
   * @param {HTMLElement} item
   * @returns {Map<number, string>} position → selected value
   */
  #selectedOptions(item) {
    /** @type {Map<number, string>} */
    const selected = new Map();
    for (const input of item.querySelectorAll('input[data-bundle-option]:checked')) {
      const el = /** @type {HTMLInputElement} */ (input);
      selected.set(Number(el.dataset.bundleOption), el.value);
    }
    return selected;
  }

  /**
   * Finds the variant matching every rendered option. Options that are not rendered (neither
   * colour nor size) are unconstrained, so prefer an available match.
   * @param {ItemState} state
   * @param {Map<number, string>} selected
   * @returns {BundleVariant | null}
   */
  #resolveVariant(state, selected) {
    const matches = state.variants.filter((variant) =>
      state.positions.every((position) => {
        const value = selected.get(position);
        return value === undefined || variant.options[position - 1] === value;
      })
    );
    return matches.find((v) => v.available) || matches[0] || null;
  }

  /**
   * @param {HTMLElement} item
   * @param {ItemState} state
   * @param {BundleVariant} variant
   */
  #checkVariant(item, state, variant) {
    for (const position of state.positions) {
      for (const input of item.querySelectorAll(`input[data-bundle-option="${position}"]`)) {
        const el = /** @type {HTMLInputElement} */ (input);
        el.checked = el.value === variant.options[position - 1];
      }
    }
  }

  /**
   * Re-renders an item from its resolved variant: image, price, selected-value labels and the
   * OOS state of every option (same hierarchy Shopify uses: an option value is available when a
   * variant with that value AND the selections of the options before it is available).
   * @param {HTMLElement} item
   */
  #syncItem(item) {
    const state = this.#items.get(item);
    if (!state) return;
    const { variant, variants, positions } = state;
    const selected = this.#selectedOptions(item);

    item.dataset.variantId = variant ? String(variant.id) : '';
    item.dataset.available = String(Boolean(variant?.available));

    const price = item.querySelector('[data-bundle-item-price]');
    if (price && variant) price.textContent = this.#money(variant.price);

    const image = /** @type {HTMLImageElement | null} */ (item.querySelector('[data-bundle-item-image]'));
    if (image && variant?.image && image.getAttribute('src') !== variant.image) {
      image.src = variant.image;
    }

    for (const position of positions) {
      const label = item.querySelector(`[data-bundle-option-value="${position}"]`);
      if (label) label.textContent = selected.get(position) || '';

      const before = positions.filter((p) => p < position);
      for (const input of item.querySelectorAll(`input[data-bundle-option="${position}"]`)) {
        const el = /** @type {HTMLInputElement} */ (input);
        const available = variants.some(
          (v) =>
            v.available &&
            v.options[position - 1] === el.value &&
            before.every((p) => selected.get(p) === undefined || v.options[p - 1] === selected.get(p))
        );
        el.dataset.optionAvailable = String(available);
        if (available) {
          el.removeAttribute('aria-disabled');
        } else {
          el.setAttribute('aria-disabled', 'true');
        }

        // Same rule as the PDP: strike a colour that is in stock but not with the other selections.
        if (!el.closest('.variant-option__button-label--has-swatch')) continue;
        const buyable =
          !available ||
          variants.some(
            (v) =>
              v.available &&
              positions.every((p) =>
                p === position ? v.options[p - 1] === el.value : selected.get(p) === undefined || v.options[p - 1] === selected.get(p)
              )
          );
        if (buyable) {
          delete el.dataset.selectionAvailable;
        } else {
          el.dataset.selectionAvailable = 'false';
        }
      }
    }
  }

  /**
   * Recomputes a bundle's totals (drawer header, drawer CTA, card CTA) and the CTA's
   * disabled state. The total is the plain sum of the selected variants — no bundle discount.
   * @param {HTMLElement} panel
   */
  #updatePanel(panel) {
    let total = 0;
    let compareTotal = 0;
    let allAvailable = true;
    let count = 0;

    for (const item of panel.querySelectorAll('[data-bundle-item]')) {
      const variant = this.#items.get(/** @type {HTMLElement} */ (item))?.variant;
      count += 1;
      if (!variant || !variant.available) allAvailable = false;
      if (!variant) continue;
      total += variant.price;
      compareTotal += Math.max(variant.compare_at_price || 0, variant.price);
    }

    const formatted = this.#money(total);
    const totalEl = panel.querySelector('[data-bundle-total]');
    if (totalEl) totalEl.textContent = formatted;

    const priceEl = /** @type {HTMLElement | null} */ (panel.querySelector('[data-bundle-price]'));
    const compareEl = /** @type {HTMLElement | null} */ (panel.querySelector('[data-bundle-compare]'));
    const labelEl = /** @type {HTMLElement | null} */ (panel.querySelector('[data-bundle-atc-label]'));
    const soldOutEl = /** @type {HTMLElement | null} */ (panel.querySelector('[data-bundle-sold-out]'));
    const soldOut = count > 0 && !allAvailable;
    const onSale = compareTotal > total;
    if (priceEl) {
      priceEl.textContent = formatted;
      priceEl.classList.toggle('bundle-module-drawer__atc-price--sale', onSale);
      priceEl.hidden = soldOut;
    }
    if (compareEl) {
      compareEl.textContent = this.#money(compareTotal);
      compareEl.hidden = !onSale || soldOut;
    }
    if (labelEl) labelEl.hidden = soldOut;
    if (soldOutEl) soldOutEl.hidden = !soldOut;

    const bundleId = panel.dataset.bundlePanel || '';
    const cardPrice = this.querySelector(`[data-bundle-card-price="${CSS.escape(bundleId)}"]`);
    if (cardPrice) cardPrice.textContent = formatted;

    const atc = /** @type {HTMLButtonElement | null} */ (panel.querySelector('[data-bundle-atc]'));
    if (atc) atc.disabled = !allAvailable || count === 0;
  }

  /** @returns {Promise<any>} The AJAX cart. */
  async #refreshCart() {
    /** @type {(HTMLElement & { fetchCartData?: () => Promise<any> }) | null} */
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      if (typeof cartItemsComponent.fetchCartData === 'function') {
        return cartItemsComponent.fetchCartData();
      }
    }

    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    return response.json();
  }

  /** @param {HTMLButtonElement} button */
  async #addBundleToCart(button) {
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const panel = /** @type {HTMLElement | null} */ (button.closest('[data-bundle-panel]'));
    if (!panel) return;

    const bundleId = panel.dataset.bundlePanel || '';
    const bundleName = panel.dataset.bundleName || '';
    const errorEl = /** @type {HTMLElement | null} */ (panel.querySelector('[data-bundle-error]'));
    if (errorEl) errorEl.hidden = true;

    /** @type {Array<{id: number, quantity: number, properties: Record<string, string>}>} */
    const items = [];
    /** @type {Array<{productId: number, variantId: number, quantity: number, price: number}>} */
    const trackedItems = [];
    let total = 0;
    for (const item of panel.querySelectorAll('[data-bundle-item]')) {
      const el = /** @type {HTMLElement} */ (item);
      const variant = this.#items.get(el)?.variant;
      if (!variant || !variant.available) return;
      total += variant.price;
      items.push({
        id: variant.id,
        quantity: 1,
        properties: { _bundle_module_id: bundleId, _bundle_module_name: bundleName },
      });
      trackedItems.push({ productId: Number(el.dataset.productId), variantId: variant.id, quantity: 1, price: variant.price });
    }
    if (!items.length) return;

    const analytics = { bundleId, bundleName, sectionId: this.#sectionId };

    const sectionIds = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    }

    button.setAttribute('aria-busy', 'true');

    // Dispatched from inside the bundle's modal <dialog>: cart-drawer.js defers its auto-open
    // until that dialog's `close` fires, so the mini-cart opens as the bundle drawer closes.
    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    const itemCount = items.length;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();

      if (data.status) {
        button.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        const message = data.description || data.message || 'Could not add this bundle to your cart.';
        this.#emit('goat:bundle:add-to-cart-error', { ...analytics, message });
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
        }
        this.#refreshCart()
          .then((cart) =>
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: { didError: true, items: cart.items, source: 'bundle-module', sourceId: bundleId, itemCount },
            })
          )
          .catch(deferred.reject);
        return;
      }

      this.#emit('goat:bundle:add-to-cart', { ...analytics, currency: this.#currency, total, items: trackedItems });

      // The lines are already in the cart, so a failed re-read must not surface as a failed add.
      let cart = null;
      try {
        cart = await this.#refreshCart();
      } catch (error) {
        console.error(error);
      }

      if (cart) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'bundle-module',
            sourceId: bundleId,
            itemCount,
            sections: data.sections,
            didError: false,
          },
        });

        this.#emit('goat:bundle:cart-lines', {
          ...analytics,
          currency: this.#currency,
          lines: (cart.items || [])
            .filter((/** @type {any} */ line) => line.properties?._bundle_module_id === bundleId)
            .map((/** @type {any} */ line) => ({
              key: line.key,
              productId: line.product_id,
              variantId: line.variant_id,
              quantity: line.quantity,
              price: line.final_price,
            })),
        });
      } else {
        deferred.resolve({
          cart: null,
          detail: { items: data.items, source: 'bundle-module', sourceId: bundleId, itemCount, sections: data.sections, didError: false },
        });
      }

      // Let cart-drawer.js's promise handler attach its dialog `close` listener first.
      await new Promise((resolve) => setTimeout(resolve, 0));
      await this.#drawer?.close?.();
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.#emit('goat:bundle:add-to-cart-error', {
        ...analytics,
        message: error instanceof Error ? error.message : 'Network error during add to cart',
      });
      button.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      if (errorEl) {
        errorEl.textContent = 'Could not add this bundle to your cart.';
        errorEl.hidden = false;
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('bundle-module-component')) {
  customElements.define('bundle-module-component', BundleModuleComponent);
}
